import React from 'react';
import styles from './SurveySearchPage.module.css';
import { formatGermanDate, toInputDate } from './surveySearchHelpers';
import ParticipantSelector from './ParticipantSelector';
import KeyValuePairs from './KeyValuePairs';
import ToggleSwitch from '../../../components/ToggleSwitch';

function FilterSection({
  queryParams,
  setQueryParams,
  tInput,
  setTInput,
  lfdnInput,
  setLfdnInput,
  participantSearch,
  setParticipantSearch,
  showParticipantDropdown,
  setShowParticipantDropdown,
  keyValuePairs,
  setKeyValuePairs,
  onlyCollisions,
  setOnlyCollisions,
  variablesMapping,
  completedSurveysInput,
  setCompletedSurveysInput,
}) {
  const handleTChange = (value) => {
    setTInput(value);
    const tValues = value
      .split(',')
      .map((v) => parseInt(v.trim()))
      .filter((v) => !isNaN(v) && v >= 1 && v <= 8);
    setQueryParams((prev) => ({ ...prev, t: tValues }));
  };

  const handleLfdnChange = (value) => {
    setLfdnInput(value);
    const lfdnValues = value
      .split(',')
      .map((v) => parseInt(v.trim()))
      .filter((v) => !isNaN(v) && v > 0);
    setQueryParams((prev) => ({ ...prev, lfdn: lfdnValues }));
  };

  const handleDateChange = (field, value) =>
    setQueryParams((prev) => ({ ...prev, [field]: value }));

  // Handle toggle switch
  const handleOnlyCollisionsToggle = (e) => {
    setOnlyCollisions(e.target.checked);
    setQueryParams((prev) => ({
      ...prev,
      only_collisions: e.target.checked,
      page: 1,
    }));
  };

  const handleCompletedSurveysChange = (value) => {
    setCompletedSurveysInput(value);
    const completedSurveysValues = value
      .split(',')
      .map((v) => parseInt(v.trim()))
      .filter((v) => !isNaN(v) && v >= 1 && v <= 8);
    setQueryParams((prev) => ({
      ...prev,
      completed_surveys: completedSurveysValues,
    }));
  };

  return (
    <div className={styles.filterBox}>
      <div className={styles.filterHeader}>
        <h3 className={styles.filterTitle}>Search Filters</h3>
        <ToggleSwitch
          label="Only Collisions"
          checked={onlyCollisions}
          onChange={handleOnlyCollisionsToggle}
        />
      </div>

      <div className={styles.grid2col}>
        <div>
          <label className={styles.label}>Survey Round (1-8):</label>
          <div className={styles.flexRow}>
            <input
              type="text"
              placeholder="e.g., 1,3,5"
              value={tInput}
              onChange={(e) => handleTChange(e.target.value)}
              className={styles.input}
            />
          </div>
          <small className={styles.helpText}>
            Please indicate the survey you are interested in. To display
            multiple surveys, separate them by comma. Possible numbers are 1 to
            8.
          </small>
        </div>
        <div>
          <label className={styles.label}>Data Position (LFDN):</label>
          <div className={styles.flexRow}>
            <input
              type="text"
              placeholder="e.g., 100,200,300"
              value={lfdnInput}
              onChange={(e) => handleLfdnChange(e.target.value)}
              className={styles.input}
            />
          </div>
          <small className={styles.helpText}>
            The data position (lfdn) refers to a row within a csv file. LFDN is
            stored in the 'A' column of a Tivian csv export. Use comma to filter
            multiple LFDNs.
          </small>
        </div>
      </div>

      <div className={styles.grid2col}>
        <div>
          <label className={styles.label}>Created At (Range):</label>
          <div className={styles.flexRow}>
            <input
              type="date"
              value={toInputDate(queryParams.createdAtStart)}
              onChange={(e) =>
                handleDateChange('createdAtStart', e.target.value)
              }
              className={styles.input}
            />
            <span>to</span>
            <input
              type="date"
              value={toInputDate(queryParams.createdAtEnd)}
              onChange={(e) => handleDateChange('createdAtEnd', e.target.value)}
              className={styles.input}
            />
          </div>
          <small className={styles.helpText}>Filter by creation date.</small>
          <div className={styles.dateInfo}>
            {queryParams.createdAtStart && (
              <span>From: {formatGermanDate(queryParams.createdAtStart)} </span>
            )}
            {queryParams.createdAtEnd && (
              <span>To: {formatGermanDate(queryParams.createdAtEnd)}</span>
            )}
          </div>
        </div>
        <div>
          <label className={styles.label}>Uploaded At (Range):</label>
          <div className={styles.flexRow}>
            <input
              type="date"
              value={toInputDate(queryParams.uploadedAtStart)}
              onChange={(e) =>
                handleDateChange('uploadedAtStart', e.target.value)
              }
              className={styles.input}
            />
            <span>to</span>
            <input
              type="date"
              value={toInputDate(queryParams.uploadedAtEnd)}
              onChange={(e) =>
                handleDateChange('uploadedAtEnd', e.target.value)
              }
              className={styles.input}
            />
          </div>
          <small className={styles.helpText}>Filter by upload date.</small>
          <div className={styles.dateInfo}>
            {queryParams.uploadedAtStart && (
              <span>
                From: {formatGermanDate(queryParams.uploadedAtStart)}{' '}
              </span>
            )}
            {queryParams.uploadedAtEnd && (
              <span>To: {formatGermanDate(queryParams.uploadedAtEnd)}</span>
            )}
          </div>
        </div>
      </div>

      <div className={styles.grid2col}>
        <div>
          <ParticipantSelector
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            selectedParticipants={queryParams.participant}
            participantSearch={participantSearch}
            setParticipantSearch={setParticipantSearch}
            showParticipantDropdown={showParticipantDropdown}
            setShowParticipantDropdown={setShowParticipantDropdown}
          />
        </div>
        <div>
          <label className={styles.label}>
            Full Startup Response Complete:
          </label>
          <div className={styles.flexRow}>
            <input
              type="text"
              placeholder="e.g., 1,2"
              value={completedSurveysInput}
              onChange={(e) => handleCompletedSurveysChange(e.target.value)}
              className={styles.input}
            />
          </div>
          <small className={styles.helpText}>
            Enter the survey round numbers (1-8, comma seperated) that both
            founders of a startup must have completed.
          </small>
        </div>
      </div>

      <KeyValuePairs
        keyValuePairs={keyValuePairs}
        setKeyValuePairs={setKeyValuePairs}
        setQueryParams={setQueryParams}
        variablesMapping={variablesMapping}
      />
    </div>
  );
}

export default FilterSection;
